import { useEffect } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getPartnerConfig, partnersQueries } from '@/api/partners.service';

import SESSION_TOKEN_SERVICE from '@/constants/SessionTokenService';

import EAppRoute from '@/types/appRoutes';

import { PartnerDto } from '@/generated-types';
import useCustomParams from '@/shared/hooks/useCustomParams';

const useInitializeApp = (): {
	isLoading: boolean;
	partnerData?: Required<PartnerDto>;
} => {
	const posthog = usePostHog();
	const params = useCustomParams();

	const location = useLocation();
	const navigate = useNavigate();

	const { isLoading, error, data } = useSuspenseQuery({
		queryKey: partnersQueries.partner.me.queryKey,
		queryFn: () => getPartnerConfig(params.partner as string),
		retry: 0,
	});

	useEffect(() => {
		if (!error) {
			return;
		}

		navigate(`${EAppRoute.NotFound}${location.search}`, { replace: true });
	}, [error, navigate]);

	useEffect(() => {
		if (!data) {
			return;
		}

		if (process.env.DEFAULT_PARTNER === 'loanbud' && !data.isLoanbudPartner) {
			navigate(EAppRoute.NotFound, { replace: true });
			return;
		}

		// Remove credentials left by the old apply-form implementation. A browser session may only
		// retain the new draft-scoped token; partner API keys must never be read from public config.
		const existingToken = SESSION_TOKEN_SERVICE.getLocalAccessToken();
		if (existingToken && !existingToken.startsWith('draft_')) {
			SESSION_TOKEN_SERVICE.removeToken();
		}

		const partnerSlug = params.partner;
		if (partnerSlug) {
			posthog?.identify(partnerSlug, {
				partnerId: data.id,
				partnerName: data.name,
			});
		}
	}, [data]);

	useEffect(() => {
		const handleUnload = (): void => {};
		window.addEventListener('unload', handleUnload);

		return () => {
			window.removeEventListener('unload', handleUnload);
		};
	}, []);

	return { isLoading, partnerData: data };
};

export default useInitializeApp;
